import { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import AdminLayout from './AdminLayout';
import { Plus, Pencil, Trash2, X, Image as ImageIcon } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import { Toaster } from '../../components/ui/sonner';

const AdminGallery = () => {
  const { token } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({ title: '', category: '', image: '' });

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const categories = [
    'Reforma de Fachada', 'Impermeabilizações', 'Manutenção Predial',
    'Teste de Carga', 'Segurança', 'Diagnóstico Técnico',
    'Inspeção com Drone 4K', 'Reforma Completa'
  ];

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/gallery`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) setItems(await response.json());
    } catch (error) {
      toast.error('Erro ao carregar galeria');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const url = editingItem 
        ? `${API_URL}/api/admin/gallery/${editingItem.id}`
        : `${API_URL}/api/admin/gallery`;
      
      const response = await fetch(url, {
        method: editingItem ? 'PUT' : 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        toast.success(editingItem ? 'Item atualizado!' : 'Item adicionado!');
        setShowModal(false);
        resetForm();
        fetchData();
      }
    } catch (error) {
      toast.error('Erro ao salvar');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Tem certeza que deseja eliminar este item?')) return;
    
    try {
      await fetch(`${API_URL}/api/admin/gallery/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      toast.success('Item eliminado!');
      fetchData();
    } catch (error) {
      toast.error('Erro ao eliminar');
    }
  };

  const openEditModal = (item) => {
    setEditingItem(item);
    setFormData({ title: item.title, category: item.category, image: item.image });
    setShowModal(true);
  };

  const resetForm = () => {
    setEditingItem(null);
    setFormData({ title: '', category: '', image: '' });
  };

  return (
    <AdminLayout>
      <Toaster position="top-right" richColors />
      
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Galeria</h1>
            <p className="text-gray-500">Gerencie as fotos de trabalhos</p>
          </div>
          <Button onClick={() => { setShowModal(true); resetForm(); }} className="bg-pink-600 hover:bg-pink-700">
            <Plus size={18} className="mr-2" />
            Nova Foto
          </Button>
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pink-600"></div>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {items.map((item) => (
              <Card key={item.id} className="overflow-hidden group">
                <div className="relative aspect-square bg-gray-100">
                  {item.image ? (
                    <img 
                      src={item.image} 
                      alt={item.title} 
                      className="w-full h-full object-cover"
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <ImageIcon size={40} className="text-gray-300" />
                    </div>
                  )}
                  <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center space-x-2">
                    <Button size="sm" variant="secondary" onClick={() => openEditModal(item)}>
                      <Pencil size={16} />
                    </Button>
                    <Button size="sm" variant="destructive" onClick={() => handleDelete(item.id)}>
                      <Trash2 size={16} />
                    </Button>
                  </div>
                </div>
                <CardContent className="p-3">
                  <h3 className="font-medium text-sm text-gray-900 truncate">{item.title}</h3>
                  <Badge variant="outline" className="mt-1 text-xs">{item.category}</Badge>
                </CardContent>
              </Card>
            ))}
            {items.length === 0 && (
              <div className="col-span-full text-center py-12 text-gray-500">Nenhum item na galeria</div>
            )}
          </div>
        )}

        <p className="text-sm text-gray-500">Total: {items.length} fotos</p>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>{editingItem ? 'Editar Foto' : 'Nova Foto'}</CardTitle>
              <Button variant="ghost" size="sm" onClick={() => setShowModal(false)}><X size={18} /></Button>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label>Título *</Label>
                  <Input value={formData.title} onChange={(e) => setFormData({ ...formData, title: e.target.value })} placeholder="Ex: Reforma de Fachada" required />
                </div>
                <div>
                  <Label>Categoria *</Label>
                  <select value={formData.category} onChange={(e) => setFormData({ ...formData, category: e.target.value })} className="w-full h-10 px-3 border rounded-md" required>
                    <option value="">Selecione...</option>
                    {categories.map(cat => <option key={cat} value={cat}>{cat}</option>)}
                  </select>
                </div>
                <div>
                  <Label>URL da Imagem *</Label>
                  <Input value={formData.image} onChange={(e) => setFormData({ ...formData, image: e.target.value })} placeholder="/gallery/work-1.jpg ou URL externa" required />
                  {formData.image && (
                    <div className="mt-2 aspect-video bg-gray-100 rounded overflow-hidden">
                      <img src={formData.image} alt="Preview" className="w-full h-full object-cover" onError={(e) => { e.target.style.display = 'none'; }} />
                    </div>
                  )}
                </div>
                <div className="flex space-x-3 pt-4">
                  <Button type="button" variant="outline" onClick={() => setShowModal(false)} className="flex-1">Cancelar</Button>
                  <Button type="submit" className="flex-1 bg-pink-600 hover:bg-pink-700">{editingItem ? 'Atualizar' : 'Adicionar'}</Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      )}
    </AdminLayout>
  );
};

export default AdminGallery;
